import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  TextField,
  Autocomplete,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import debounce from 'lodash/debounce';
import { Location } from '../../models'; // Use the model Location type
import { apiClient } from '../../services/ApiClient';
import { locationCache } from '../../services/LocationCacheService';

interface LocationSelectorProps {
  selectedLocationId: number | null; // Keep ID for internal selection state
  onLocationSelect: (location: Location | null) => void; // Callback expects full object or null
}

/**
 * Component for selecting a location using search functionality
 */
const LocationSelector: React.FC<LocationSelectorProps> = ({
  selectedLocationId,
  onLocationSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Load selected location if available - using locationCache
  useEffect(() => {
    const loadLocationData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If a location ID is already selected, fetch its details from cache first
        if (selectedLocationId !== null) {
          try {
            // Use locationCache which checks cache first before making an API call
            const locationDetails = await locationCache.fetchIfNeeded(selectedLocationId);
            
            if (locationDetails) {
              setSelectedLocation(locationDetails);
            } else {
              setError('Location not found. Please try searching for a location.');
            }
          } catch (err) {
            console.error('Error fetching selected location details:', err);
            setError('Unable to load the selected location. Please try searching for a location.');
          }
        } else {
          // If no location is selected, reset the selectedLocation
          setSelectedLocation(null);
        }
      } catch (err) {
        console.error('Error loading location data:', err);
        setError('Failed to load location details. Please try searching for a specific location.');
      } finally {
        setLoading(false);
      }
    };

    loadLocationData();
  }, [selectedLocationId]);

  // Create a debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      if (!term || term.length < 2) return; // Don't search for very short terms
      
      try {
        setLoading(true);
        setError(null);
        const results = await apiClient.searchLocations(term);
        setLocations(results);
      } catch (err) {
        console.error('Error searching locations:', err);
        setError('Failed to search locations. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 500), // 500ms debounce time
    []
  );

  // Handle search input changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    debouncedSearch(newSearchTerm);
  };

  // Handle location selection
  const handleLocationChange = (_event: React.SyntheticEvent, location: Location | null) => {
    setSelectedLocation(location); // Update internal state

    onLocationSelect(location); // Pass the full location object or null back
  };

  return (
     
      <FormControl fullWidth error={!!error}>
        <Autocomplete
          id="location-select"
          options={locations}
          loading={loading}
          value={selectedLocation}
          onChange={handleLocationChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a location"
              variant="outlined"
              onChange={handleSearchChange}
              value={searchTerm}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
        {!error && locations.length === 0 && searchTerm.length > 1 && !loading && (
          <FormHelperText>No locations found. Try a different search term.</FormHelperText>
        )}
        {!error && locations.length === 0 && searchTerm.length <= 1 && !loading && !selectedLocation && (
          <FormHelperText>
            Type at least 2 characters to search for a location. For example: "Sydney", "Melbourne", or "Brisbane".
          </FormHelperText>
        )}
      </FormControl>
  );
};

export default LocationSelector;
